import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { get, isEqual, uniq } from 'lodash';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import { Questionnaire } from '@/typings/Questionnaire';
import { usePrevious, useToast } from '@/hooks';
import evaluateExpression from '@helpers/evaluateExpression';
import getFieldValue from '@helpers/questionnaires/getFieldValue';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import * as QuestionnaireApiModels from '@typings/questionnaires-api-models';
import * as ApiModels from '@typings/api-models';
import { logError } from '@/providers/ErrorTracking';
import simpleDebug from '@/helpers/simpleDebug';
import getCountyFromAddress from '@/helpers/getCountyFromAddress';
import getListOrTableField from '@helpers/getListOrTableField';
import { useEventsContext } from '@/providers/Events';
import rerunAiAgentOnQuestionnaireField from '@/tsClient/questionnaires/rerunAiAgentOnQuestionnaireField';
import canRunAutofillOnRow from '@components/Questionnaire/form/helpers/canRunAutofillOnRow';
import parseFieldKey from '@/components/Questionnaire/form/helpers/parseFieldKey';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import isInternalAutofill from './isInternalAutofill';
import { determineType } from '../../FormField/renderPopulatedFrom';
import useExternalDataSets from './useExternalDataSets';
import areValuesEqual from '../../helpers/areValuesEqual';

type Props = {
  filter?: {
    fieldKeys: Set<string>;
    // rowIndexes: Set<number>;
  }
  formData: {
    id: string;
    sections: Parameters<typeof useExternalDataSets>[0]['sections'];
  };
  formValues: Record<string, unknown>;
  currentTableRowKey?: string | null;
};

const HandleAllAutofills: React.FC<Props> = ({
  filter,
  formData,
  formValues,
  currentTableRowKey,
}) => {
  const {
    fieldsByKey,
    fieldsById,
    userWorkflowId,
    newListItemId,
  } = useQuestionnaireContext();

  const {
    formMethods,
    getResponseSource,
    getResponseSourceIndex,
    setValue,
    formContextId,
    getCanSaveValueToDB,
  } = useQuestionnaireFormContext();
  const datasets = useExternalDataSets({ sections: formData.sections });
  const questionnaireEditListItemOnSave = useFeatureFlag('questionnaire-edit-list-item-on-save');

  const { getData: getAutofillInitialValueFn } = useNoodleApi(tsClient.formRequests.getAutofillInitialValue);
  const { getData: rerunAiAgentFn } = useNoodleApi(rerunAiAgentOnQuestionnaireField);
  const { getData: getFilingDistrict } = useNoodleApi(tsClient.ai.getFilingDistrict);

  const toast = useToast();
  const { addListener, removeListener, emit } = useEventsContext();
  const triedAutofillKeysRef = useRef<string[]>([]);

  const previousFormValues = usePrevious(formValues);

  const getTableFieldKey = useCallback((key: string): Promise<string | null> | string | null => {
    if (!key) {
      return null;
    }

    if (!fieldsByKey || Object.keys(fieldsByKey).length === 0) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(getTableFieldKey(key));
        }, 100);
      });
    }

    const fieldToFill = fieldsByKey[key];

    if (!fieldToFill) {
      return null;
    }

    if (fieldToFill.type === 'table' || fieldToFill.type === 'list') {
      return fieldToFill.key;
    }

    if (!fieldToFill.listFieldTemplateId) {
      return null;
    }

    const parentField = Object.values(fieldsByKey).find((f) => f.id === fieldToFill.listFieldTemplateId);
    if (parentField) {
      return getTableFieldKey(parentField.key);
    }
    return null;
  }, [fieldsByKey]);

  const getAutofillValue = useCallback(async ({
    autofill,
    result,
    fieldKey,
    linkedFieldKeys,
    sourceType,
    currentFormValues,
    resettingAutofill,
  }: {
    autofill: Pick<QuestionnaireApiModels.FieldTemplateAutofill, 'action'>;
    result: unknown;
    fieldKey: string;
    linkedFieldKeys: string[];
    sourceType: QuestionnaireApiModels.QuestionnaireResponse['source'];
    currentFormValues: Record<string, unknown>;
    resettingAutofill?:boolean;
  }): Promise<void> => {
    simpleDebug(fieldKey, 'in getAutofillValue', autofill, fieldKey, result);
    // Part 1. Process the result based on action type
    let processedResult: unknown = null;

    /** always mark autofill as attempted for internal autofills and AI agent */
    const autofillAttempted = autofill.action === 'conditional-fill'
      || autofill.action === 'fill-from-dataset'
      || autofill.action === 'calculation'
      || autofill.action === 'ai-agent';

    // Bankruptcy court ai lookup
    if (autofill.action === 'get-filing-district') {
      if (typeof result === 'string' && result.length === 5) {
        if (getResponseSource(fieldKey) === 'manual' && !resettingAutofill) {
          simpleDebug(fieldKey, 'not updating, source=manual');
          return;
        }
        const { data: filingDistrict } = await getFilingDistrict({ fieldKey, questionnaireId: formData.id, zipCode: result });
        processedResult = filingDistrict;
      }
    }

    if (autofill.action === 'get-filing-county') {
      if (getResponseSource(fieldKey) === 'manual' && !resettingAutofill) {
        simpleDebug(fieldKey, 'not updating, source=manual');
        return;
      }
      if (Array.isArray(result)) {
        processedResult = await Promise.all(
          result.map(async (zipcode) => {
            const county = await getCountyFromAddress({
              address: null,
              city: null,
              state: null,
              zipcode,
            });
            return county;
          }),
        );
      }
    }

    // conditional fill, fill from dataset, and calculation checks
    if (autofill.action === 'conditional-fill'
        || autofill.action === 'fill-from-dataset'
        || autofill.action === 'calculation') {
      processedResult = result;
    }

    // Part 2: Update form values based on processed result
    const tableFieldKey = await getTableFieldKey(fieldKey);

    // update a single field value
    const updateFieldValue = ({
      fullFieldKey,
      index,
      value,
      isNewListItem = false,
    }: {
      fullFieldKey: string,
      index: number | null,
      value: unknown,
      isNewListItem?: boolean,
    }) : void => {
      if (getResponseSource(fullFieldKey) !== 'manual' || resettingAutofill) {
        /**
         * canSaveToDB is used to handle new modal changes so we don't autosave list record
         * as soon as we click on add item in the list
         */
        if ((currentTableRowKey && tableFieldKey && questionnaireEditListItemOnSave)) {
          /**
           * Only set autofill values for the current open modal
          */
          const fieldTableRowKey = `${tableFieldKey}[${index}]`;
          if (currentTableRowKey === fieldTableRowKey) {
            setValue({ hasAutofillRun: autofillAttempted, key: fullFieldKey, source: sourceType, value });
          } else if (isNewListItem) {
            const { fieldKey: fieldKeyFromParse } = parseFieldKey(fullFieldKey);
            setValue({
              hasAutofillRun: autofillAttempted,
              key: `${currentTableRowKey}.${fieldKeyFromParse}`,
              source: sourceType,
              value,
            });
          }
        } else {
          simpleDebug(fieldKey, `updating field ${fullFieldKey}`, value);
          setValue({ hasAutofillRun: autofillAttempted, key: fullFieldKey, source: sourceType, value });
        }
      } else {
        simpleDebug(fieldKey, 'not updating, source=manual', fullFieldKey);
      }
    };

    // update a field and its linked fields
    const updateFieldAndLinked = (baseKey: string, value: unknown, index: number | null, isNewListItem = false) : void => {
      updateFieldValue({ fullFieldKey: baseKey, index, isNewListItem, value});
      linkedFieldKeys.forEach(lfk => {
        const linkedKey = baseKey === fieldKey ? lfk : baseKey.replace(fieldKey, lfk);
        updateFieldValue({fullFieldKey: linkedKey, index, value});
      });
    };

    const { listFieldKey, rowIndex, fieldKey: fieldKeyFromParse } = parseFieldKey(fieldKey);
    if (rowIndex !== null && listFieldKey !== null && fieldKeyFromParse !== null) {
      // fieldKey is a fullFieldKey (eg "theListKey[0].subFieldKey")
      if (Array.isArray(processedResult)) {
        const targetValue = processedResult[rowIndex];
        if (targetValue !== undefined && targetValue !== null) {
          const fullFieldKey = `${listFieldKey}[${rowIndex}].${fieldKeyFromParse}`;
          updateFieldAndLinked(fullFieldKey, targetValue, rowIndex);
        }
      }
    } else if (tableFieldKey) {
      // fieldKey is in a list/table (eg "subFieldKey")
      if (Array.isArray(processedResult)) {
        // handle table fields with multiple values
        processedResult.forEach((value, index) => {
          // only update if value exists
          if (value !== undefined && value !== null) {
            const fullFieldKey = `${tableFieldKey}[${index}].${fieldKey}`;
            const initialValues = formMethods.getValues()?.[tableFieldKey]?.[index];
            const isNewListItem = initialValues?._id === newListItemId;
            /**
             * We can skip updating the field if all these conditions are met:
             * - if the row is a zero'd account (canRunAutofillOnRow returns false)
             * - if the row is not a new list item (isNewListItem is false)
             */
            const isZerodAccount = initialValues && !canRunAutofillOnRow(initialValues);
            /**
             * canRunAutofill acts as a bypass to help autofills run for new list items
             *
             * We always run autofills for non zero'd accounts
             * we also need to run autofill for new list items
             *
             * `isNewListItem` is check used to determine new list item in new modal where we don't autosave until save is clicked
             * `getCanSaveValueToDB` is used to check older modal which supports autosave
             */
            const canRunAutofill = isNewListItem || !getCanSaveValueToDB();
            if (
              isZerodAccount && !canRunAutofill) {
              return;
            }
            updateFieldAndLinked(fullFieldKey, value, index, canRunAutofill);
          }
        });
      } else {
        // Set the value in all rows
        const allListRows = currentFormValues[tableFieldKey] ?? [];
        if (Array.isArray(allListRows)) {
          allListRows.forEach((_value, index) => {
            const fullFieldKey = `${tableFieldKey}[${index}].${fieldKey}`;
            updateFieldAndLinked(fullFieldKey, processedResult, index);
          });
        }
      }
    } else {
      // fieldKey is a normal field key (eg "fieldKey")
      updateFieldAndLinked(fieldKey, processedResult, null);
    }
  }, [
    formData.id,
    getTableFieldKey,
    setValue,
    getResponseSource,
    getFilingDistrict,
  ]);

  const handleAutofill = useCallback(({ field, isInitialFill, resettingAutofill }: {
      field: Questionnaire.Field;
      isInitialFill?: boolean;
      resettingAutofill?: boolean;
    }) => {
    const parsedField = parseFieldKey(field.key);
    if (
      isInternalAutofill(field.autofill)
      && field.autofill.expression
      && (
        !filter
        || filter.fieldKeys.has(parsedField.fieldKey)
      )
    ) {
      try {
        const autofillVariables = (field.autofill.expression.variables ?? [])
          .filter((v) => v.referenceType === 'field-template-key')
          .map(v => v.referenceId)
          .map((v) => get(formMethods.getValues(), v));

        const previousAutofillVariables = (field.autofill.expression.variables ?? [])
          .filter((v) => v.referenceType === 'field-template-key').map(v => v.referenceId)
          .map((v) => previousFormValues?.[v]);

        const haveValuesChanged = !isEqual(autofillVariables, previousAutofillVariables);
        const fieldSource = getResponseSource(field.key);
        simpleDebug(field.key, 'trying to autofill', {
          field,
          fieldSource,
          hasAutofill: !!field.autofill,
          haveValuesChanged,
        });
        if (
          field.autofill
          && (fieldSource !== 'manual' || resettingAutofill)
          && (haveValuesChanged || isInitialFill || autofillVariables.length === 0)
        ) {
          simpleDebug(field.key, 'doing autofill calculation');
          const variablesWithValues: Array<{ variableName: string; value: unknown; selector: string; }> = [
            ...(field.autofill.expression.variables ?? [])
              .filter((v) => v.referenceType === 'field-template-key')
              .map((variable, idx) => ({
                ...variable,
                value: getFieldValue({
                  field: fieldsByKey[variable.referenceId],
                  value: autofillVariables[idx],
                }),
              })),

            // handle field-template-list-id-map variables
            ...(field.autofill.expression.variables ?? [])
              .filter((v) => v.referenceType === 'field-template-list-id-map')
              .map((variable) => {
                const variableValue = formValues[variable.referenceId];
                return {
                  ...variable,
                  value: Array.isArray(variableValue)
                    ? variableValue.reduce<Record<string, unknown>>((acc, item) => ({
                      ...acc,
                      ...(item?._id ? { [item._id]: item } : {}),
                    }), {})
                    : {},
                };
              }),
            ...(field.autofill.expression.variables ?? [])
              .filter((v) => v.referenceType === 'data-reference-slug')
              .map((v) => ({
                ...v,
                value: datasets[v.referenceId],
              })),
          ];
          simpleDebug(field.key, 'filterExpression-variablesWithValues', variablesWithValues);

          const variableValues: Record<string, unknown> = variablesWithValues.reduce((acc, variable) => {
            let {value} = variable;
            if (variable.selector) {
              // Do not map through array if the selector tries to pull off an item at a particular index of the array
              if (Array.isArray(value) && variable.selector[0] !== '[') {
                // array case - map through array and get value using selector
                value = value.map(item => get(item, variable.selector, null));
              } else {
                value = get(value, variable.selector, null);
              }
            }
            return {
              ...acc,
              [variable.variableName]: value,
            };
          }, {});

          if (
            field.autofill.expression.filterExpression
            && field.autofill.expression.filterExpressionOperand
            && field.autofill.expression.filterExpressionVariableName
          ) {
            const variableToFilter = variableValues[field.autofill.expression.filterExpressionOperand];
            simpleDebug(field.key, 'filterExpression-variableToFilter', variableToFilter);
            if (variableToFilter) {
              if (!Array.isArray(variableToFilter)) {
                logError(new Error('Attempting to use filterExpression on non-array variable'), {
                  fieldKey: field.key,
                  filterExpression: field.autofill.expression.filterExpression,
                  filterExpressionOperand: field.autofill.expression.filterExpressionOperand,
                  filterExpressionVariableName: field.autofill.expression.filterExpressionVariableName,
                  flow: 'questionnaires',
                  variableToFilter: JSON.stringify(variableToFilter),
                });
                return;
              }
              simpleDebug(field.key, 'filterExpression-expression', variableToFilter);
              const filteredRows = variableToFilter
                .filter((__, currentIndex) => Boolean(evaluateExpression({
                  expression: field.autofill?.expression?.filterExpression || '',
                  id: 'FormContent-handleAutofill-filterExpression',
                  variables: {
                    ...variableValues,
                    currentIndex,
                  },
                })))
                // @todo - Should this be in the filter expression instead?
                .filter(v => canRunAutofillOnRow(v));
              simpleDebug(field.key, 'filterExpression-filteredRows', filteredRows);

              const filteredValues = filteredRows
                .map((v) => field.autofill?.expression?.filterExpressionItemSelector
                  ? get(v, field.autofill.expression.filterExpressionItemSelector)
                  : v);
              simpleDebug(field.key, 'filterExpression-filteredValues', filteredValues);

              let filterVariableValue;
              if (field.autofill?.expression?.filterExpressionReducer === 'sum') {
                filterVariableValue = filteredValues.reduce((acc, curr) => acc + (curr ?? 0), 0);
              } else {
                filterVariableValue = filteredValues;
              }
              simpleDebug(field.key, 'filterExpression-filterVariableValue', filterVariableValue);
              variableValues[field.autofill.expression.filterExpressionVariableName] = filterVariableValue;
            }
          }

          simpleDebug(field.key, 'variableValues', variableValues);

          let result: unknown;
          if(field.autofill?.expression?.variables.some(v => v.referenceType === 'field-template-list-id-map')) {
            const otherlistVariable = field.autofill.expression.variables.find(v => (
              v.referenceType === 'field-template-key'
              && ['list', 'table'].includes(fieldsByKey[v.referenceId].type)
            ))?.variableName;
            if (otherlistVariable && variableValues[otherlistVariable]) {
              result = (variableValues[otherlistVariable] as unknown[]).map((_item, index) => evaluateExpression({
                expression: field.autofill!.expression!.expression,
                id: 'FormContent-handleAutofill-listVariable',
                variables: { ...variableValues, currentIndex: index },
              }));
            }
          } else {
            let hasRun = false;
            if (field.autofill.expression.expression.includes('[currentIndex]')) {
              const listOrTableParent = getListOrTableField({ fieldId: field.id, fieldsById });
              if (listOrTableParent) {
                const parentExpressionVariable = field.autofill.expression.variables.find(v => v.referenceId === listOrTableParent.key);
                if (parentExpressionVariable && variableValues[parentExpressionVariable.variableName]) {
                  result = (variableValues[parentExpressionVariable.variableName] as unknown[]).map((_item, index) => evaluateExpression({
                    expression: field.autofill!.expression!.expression,
                    id: 'FormContent-handleAutofill-tableOrListVariable',
                    variables: { ...variableValues, currentIndex: index },
                  }));
                  hasRun = true;
                }
              }
            }
            if (!hasRun) {
              result = evaluateExpression({
                expression: field.autofill.expression.expression,
                id: 'FormContent-handleAutofill-final',
                variables: variableValues,
              });
            }
          }

          simpleDebug(field.key, 'result', { result });

          if (result) {
            const { autofill } = field;
            const getData = async (): Promise<void> => {
              await getAutofillValue({
                autofill,
                currentFormValues: formValues,
                fieldKey: field.key,
                linkedFieldKeys: field.linkedFieldKeys ?? [],
                resettingAutofill,
                result,
                sourceType: determineType(field.autofill) ?? 'unknown',
              });
            };
            getData();
          } else {
            setValue({
              hasAutofillRun: true,
              key: field.key,
              source: determineType(field.autofill) ?? 'unknown',
              value: null,
            });
          }
        } else {
          simpleDebug(field.key, 'not autofilling', {
            fieldSource: getResponseSource(field.key),
            hasAutofill: !!field.autofill,
            numVariables: autofillVariables.length,
            valuesChanged: haveValuesChanged,
          });
        }
      } catch (error) {
        simpleDebug(field.key, 'autofill error', field.key, error);
        logError(error, { flow: 'questionnaires' });
      }
    }
  }, [
    datasets,
    fieldsByKey,
    fieldsById,
    formValues,
    getAutofillValue,
    getResponseSource,
    previousFormValues,
    formMethods,
    filter,
  ]);

  const hasFormValue = useCallback((key: string) => {
    const formValue = formValues[key];
    if (!formValue) {
      return false;
    }
    const field = fieldsByKey[key];
    if (field.type === 'currency') {
      return (
        typeof formValue === 'object'
        && formValue
        && 'amount' in formValue
        && formValue.amount !== null
        && formValue.amount !== undefined
      );
    }
    return Boolean(formValue);
  }, [formValues, fieldsByKey]);

  // Map that contains a list of all fields whose autofills should be triggered when a particular field value changes
  const autofillMap = useMemo(() => {
    const map: Record<string, Array<string>> = {};
    Object.values(fieldsByKey).forEach(field => {
      const variables = field.autofill?.expression?.variables ?? [];
      variables.forEach(variable => {
        if (
          variable.referenceType === 'field-template-key'
          || variable.referenceType === 'field-template-list-id-map'
        ) {
          if (map[variable.referenceId]) {
            map[variable.referenceId] = [...map[variable.referenceId], field.key];
          } else {
            map[variable.referenceId] = [field.key];
          }
        }
      });
    });
    return map;
  }, [fieldsByKey]);

  const referenceTypeProvider = (fieldLocal: Questionnaire.Field | Questionnaire.FieldCommon): string => {
    if(fieldLocal.autofill?.populateFrom === 'glade-questionnaire') {
      return 'noodle-questionnaire';
    }

    return fieldLocal?.autofill?.expression?.variables?.[0].referenceType || '';

  };

  const referenceIdProvider = (fieldLocal: Questionnaire.Field | Questionnaire.FieldCommon): string => {
    if(fieldLocal.autofill?.populateFrom === 'glade-questionnaire') {
      let refId = '';
      fieldLocal?.autofill?.expression?.variables?.forEach((v) => {
        if(v?.referenceType === 'noodle-questionnaire') {
          refId= v?.referenceId;
        }
      });
      return refId;
    }

    return fieldLocal?.autofill?.expression?.variables?.[0].referenceId ?? '';

  };

  const buildKeyWithSourceIndex = (fieldKey: string, sourceIndex: number): string => fieldKey.replace(/\[\d+\]/, `[${sourceIndex}]`);

  useEffect(() => {
    const listener = addListener('reRunAutofill', async ({field, contextId: heardContextId}) => {
      try {
        if (formContextId !== heardContextId) return;
        if (field.autofill?.action !== 'ai-agent') {
          emit("autofillRerunning", { contextId: heardContextId, fieldId: field.id, status: true });
        }
        if(field?.autofill?.action === 'initial-values' ) {
          const params={
            questionnaireId: formData.id,
            referenceId: referenceIdProvider(field),
            referenceType: referenceTypeProvider(field),
            userWorkflowId: userWorkflowId ?? '',
          };
          const sourceType = determineType(field.autofill) ?? 'unknown';
          const {data} = await getAutofillInitialValueFn(params);
          if(!data) {
            setValue({ hasAutofillRun: true, key: field.key, source: sourceType || 'initial-values', value: null });
            return;
          }

          const sourceIndex = getResponseSourceIndex(field.key);

          let getAutofillDataFieldKey: string = field.key;
          if (sourceIndex !== null) {
            getAutofillDataFieldKey = buildKeyWithSourceIndex(field.key, sourceIndex);
          }
          const value = get(data, getAutofillDataFieldKey, null);

          setValue({ hasAutofillRun: true, key: field.key, source: sourceType, value });
        }

        else{
          handleAutofill({field: field as Questionnaire.Field, isInitialFill:true, resettingAutofill:true });
        }
      } catch (error) {
        toast(useToast.ToastTypeVariants.ERROR, 'Autofill re-run failed!');
      } finally{
        /** AI Agent autofill reruns are handled in HandleAiAgentAutofill Component */
        if(field.autofill?.action !== 'ai-agent') {
          emit("autofillRerunning", { contextId: heardContextId, fieldId: field.id, status: false});
        }
      }
    });
    return () => removeListener('reRunAutofill', listener);
  }, [
    addListener,
    handleAutofill,
    removeListener,
    emit,
    getAutofillInitialValueFn,
    rerunAiAgentFn,
    setValue,
    toast,
    userWorkflowId,
    fieldsByKey,
    formContextId,
    formData.id,
  ]);

  useEffect(() => {
    const fieldsWithoutDependencies = Object.values(fieldsByKey)
      .filter(f => (
        f.autofill
        && isInternalAutofill(f.autofill)
        && f.autofill.expression
        && f.autofill.expression.variables.filter(v => v.referenceType === 'field-template-key').length === 0
        && (
          !getResponseSource(f.key)
          || getResponseSource(f.key) === 'data-reference-slug'
        )
        && (f.type === 'currency'
          ? !formValues[f.key] || (formValues[f.key] as unknown as ApiModels.CurrencyValue).amount === null
          : !formValues[f.key]
        )
      ));
    fieldsWithoutDependencies.forEach(field => {
      handleAutofill({ field });
    });
  }, [fieldsByKey, getResponseSource, handleAutofill, datasets, formValues]);

  useEffect(() => {
    const listKeysWithAddedRows: string[] = [];
    const changedFieldKeys = Object.keys(formValues).filter((key) => {
      const current = formValues[key];
      const previous = previousFormValues?.[key] || {};

      // Find lists with added rows
      if (Array.isArray(current) && Array.isArray(previous)) {
        if (current.length > previous.length) {
          listKeysWithAddedRows.push(key);
        }
      }

      return !areValuesEqual(current, previous);
    });

    const autofillFields = uniq(changedFieldKeys.reduce((acc: string[], key) => [
      ...acc,
      ...(autofillMap[key] || []),
    ], []))
      .map(key => fieldsByKey[key]);
    autofillFields.forEach(field => {
      const dependentDatasets = (field.autofill?.expression?.variables ?? [])
        .filter(v => v.referenceType === 'data-reference-slug')
        .map(v => v.referenceId);
      const allDatasetsExist = dependentDatasets.every(d => datasets[d]);
      if (allDatasetsExist) {
        handleAutofill({ field });
      }
    });

    // When a list changes, look at all listItemTemplates and remove keys from tried autofills
    // This allows autofills to be run on newly added list rows
    const listItemKeysToRemove = listKeysWithAddedRows.flatMap((key) => {
      const listItems = fieldsByKey[key]?.listItemTemplates ?? [];
      return listItems.map(item => item.key);
    });

    if (listItemKeysToRemove.length > 0) {
      triedAutofillKeysRef.current = triedAutofillKeysRef.current.filter(
        key => !listItemKeysToRemove.includes(key),
      );
    }
  }, [getResponseSource, formValues, previousFormValues, fieldsByKey, formMethods, getAutofillValue, datasets, autofillMap, handleAutofill]);

  // Try to fill dependent autofills that haven't been filled once we load all dependent fields in fieldsByKey
  useEffect(() => {
    const fieldsWithAutofillDependencies = Object.values(fieldsByKey).filter((f) =>
      !triedAutofillKeysRef.current.includes(f.key)
      && isInternalAutofill(f.autofill)
      && f.autofill.expression
      && f.autofill.expression.variables.filter(v => v.referenceType === 'field-template-key').length > 0
      && (!getResponseSource(f.key) || (getResponseSource(f.key) === 'data-reference-slug' && !hasFormValue(f.key))));

    const fieldsWithAllDependenciesLoaded = fieldsWithAutofillDependencies.filter((f) => {
      const dependentFieldKeys = f.autofill?.expression?.variables
        ? f.autofill.expression.variables.filter((v) => v.referenceType === 'field-template-key').map((v) => v.referenceId)
        : [];
      const allKeysExist = dependentFieldKeys.every((key) => key in fieldsByKey);
      const allValuesExist = dependentFieldKeys.every((key) => key in formValues);

      const dependentDatasets = f.autofill?.expression?.variables?.filter(v => v.referenceType === 'data-reference-slug')?.map(v => v.referenceId) ?? [];
      const allDatasetsExist = dependentDatasets.every(d => datasets[d]);

      simpleDebug(f.key, 'checking if dependencies are loaded', {
        allDatasetsExist,
        allDependentFieldsLoaded: allKeysExist,
        allDependentValuesExist: allValuesExist,
        field: f,
      });

      return allKeysExist && allValuesExist && allDatasetsExist;
    });

    fieldsWithAllDependenciesLoaded.forEach(field => {
      simpleDebug(field.key, 'handling autofill via initial fieldByKey load', {
        field,
      });
      handleAutofill({ field, isInitialFill: true });
      triedAutofillKeysRef.current = [...triedAutofillKeysRef.current, field.key];
    });
    // intentionally don't include formValues in the dep array, want this to run as fieldsByKey is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsByKey, getResponseSource, handleAutofill, hasFormValue, datasets]);

  return null;
};

export default HandleAllAutofills;
